export default function Product() {
    return (
        <div id="products" className="my-5">
            <h4>Product JSON Structure</h4>
            <p>
                This document describes each key in the <strong>Product JSON object</strong> returned from the backend.
                It helps you understand what data fields are available for use inside Handlebars templates or API integrations.
            </p>

            <hr />

            <h5>Root-Level Keys</h5>
            <ul>
                <li><code>inventory_count</code> — Total available quantity across all variants.</li>
                <li><code>body_html</code> — The full HTML content or description of the product.</li>
                <li><code>title</code> — The main product title displayed in listings and product pages.</li>
                <li><code>handle</code> — A unique, URL-friendly identifier for the product (used in page slugs).</li>
                <li><code>product_type</code> — The product category or classification (e.g., T-shirt, Shoes).</li>
                <li><code>vender</code> — The product’s brand or supplier name.</li>
                <li><code>status</code> — Indicates if the product is currently <code>Active</code> or <code>Draft</code>.</li>
                <li><code>metafields</code> — Custom metadata attached to the product (if available).</li>
                <li><code>published</code> — Boolean flag indicating if the product is visible on the storefront.</li>
                <li><code>product_id</code> — Unique numeric ID assigned to the product.</li>
                <li><code>inventory_tracked</code> — Boolean flag to indicate whether inventory is tracked.</li>
                <li><code>slugs</code> — Optional slug references for internal routing (may be null).</li>
                <li><code>tags</code> — Array of tags for filtering, search, or categorization.</li>
            </ul>

            <hr />

            <h5>Example Usage</h5>
            <pre><code className="language-hbs">{`{{#each product}}
  <a href="/products/{{handle}}">
    {{title}}
  </a>
{{/each}}`}
            </code></pre>
            <hr />

            <h5>variants (Array)</h5>
            <p>
                Contains details about each product variant (for example, color or size variations).
            </p>
            <ul>
                <li><code>product_id</code> — The parent product’s ID that this variant belongs to.</li>
                <li><code>image_id</code> — Associated image ID for this variant (if any).</li>
                <li><code>barcode</code> — SKU barcode for inventory or scanning systems.</li>
                <li><code>compare_at_price</code> — The original price before discount.</li>
                <li><code>inventory_quantity</code> — The available stock for this variant.</li>
                <li><code>old_inventory_quantity</code> — Previously recorded inventory before update.</li>
                <li><code>position</code> — The variant’s order position in the product listing.</li>
                <li><code>option1 / option2 / option3</code> — Option names or values (e.g., Color, Size, Material).</li>
                <li><code>out_of_stock</code> — Boolean indicating if the variant is unavailable.</li>
                <li><code>price</code> — Selling price for the variant.</li>
                <li><code>sku</code> — Stock Keeping Unit code (unique identifier for variant).</li>
                <li><code>title</code> — The display title of this variant (often the value of option1).</li>
                <li><code>weight</code> — The variant’s weight.</li>
                <li><code>weight_unit</code> — The unit of measurement for weight (e.g., kg, g).</li>
                <li><code>variant_id</code> — Unique numeric ID for this specific variant.</li>
            </ul>

            <hr />

            <h5>images (Array)</h5>
            <p>
                Holds all image objects related to the product.
            </p>
            <ul>
                <li><code>id</code> — Unique ID for the image record.</li>
                <li><code>product_id</code> — The product’s ID that this image belongs to.</li>
                <li><code>position</code> — Sort order or display order of the image.</li>
                <li><code>src</code> — Direct URL path to the image asset (CDN link).</li>
            </ul>

            <hr />

            <h5>options (Array)</h5>
            <p>
                Defines configurable product options such as size, color, or material.
            </p>
            <ul>
                <li><code>product_id</code> — Parent product’s ID.</li>
                <li><code>name</code> — Option label (e.g., “Color”).</li>
                <li><code>position</code> — The display order of this option.</li>
                <li><code>values</code> — Array of all possible values for this option.</li>
            </ul>

            <hr />

            <h5>category (Array)</h5>
            <p>
                Contains one or more category objects linked to the product.
            </p>
            <ul>
                <li><code>category_id</code> — Unique numeric ID for the category.</li>
                <li><code>body_html</code> — HTML description of the category.</li>
                <li><code>path</code> — Image path or asset link for the category.</li>
                <li><code>handle</code> — URL-friendly identifier for the category.</li>
                <li><code>published_at</code> — Date and time when the category was published.</li>
                <li><code>metafields_description</code> — SEO or metadata description field.</li>
                <li><code>metafields_title</code> — SEO or metadata title field.</li>
                <li><code>title</code> — Category display name.</li>
                <li><code>template_suffix</code> — Optional template override for the category page.</li>
                <li><code>updated_at</code> — Last updated date for category details.</li>
                <li><code>mobile_image</code> — Optional mobile-specific image path.</li>
                <li><code>status</code> — Indicates if the category is active.</li>
                <li><code>Category_product</code> — Relationship object linking product and category.</li>
            </ul>

            <hr />

            <h5>Example Usage</h5>
            <pre><code className="language-hbs">{`{{#each product.variants}}
  {{title}} - ₹{{price}}
{{/each}}`}
            </code></pre>

            <p>
                These keys can be accessed directly in Handlebars templates using dot-notation (e.g. <code>{'{{ "product.title"}}'}</code>)
                or within loops for nested arrays (e.g. <code>{"{{#each product.images}} {{src}} {{/each}}"}</code>).
            </p>
        </div>
    )
}
